import { CompetitionCategory } from '../../components/competition-category';
import { CompetitionBadges } from '../../components/competition-badges';
import { Table } from '~/components/Table/Table';
import { Link } from '~/components/Link/Link';
import { CompetitionList } from '~/apis/ssf';
import { formatDate } from '~/utils/date';

interface Props {
  competitions: CompetitionList[];
  isLoading: boolean;
}

function hasCompetitionEnded(endDate: string | null | undefined): boolean {
  if (!endDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(endDate) < today;
}

function Row({ competition }: { competition: CompetitionList }) {
  // Route to results only once the competition has ended; upcoming competitions
  // (including those where registration has already closed) should land on the info page.
  const name = hasCompetitionEnded(competition.end_date) ? (
    <Link to={`${competition.id}/results`}>{competition.name}</Link>
  ) : (
    <Link to={`${competition.id}`}>{competition.name}</Link>
  );

  /**
   * If the two dates are identical, meaning the competition is hosted on a single day
   * we can instead of showing 2024-01-01 - 2024-01-01 just show one of them.
   */
  const date =
    competition.start_date === competition.end_date
      ? competition.start_date
      : `${competition.start_date} - ${competition.end_date}`;

  return (
    <Table.Row>
      <Table.Col>
        <div style={{ fontWeight: 'var(--font-weight-semibold)' }}>{name}</div>
        <div style={{ fontSize: 'var(--size-3\\.5)' }}>{competition.organizer ?? '-'}</div>
      </Table.Col>
      <Table.Col>
        <CompetitionBadges competition={competition} />
      </Table.Col>
      <Table.Col>
        <CompetitionCategory competition={competition} />
      </Table.Col>
      <Table.Col>
        {competition.reg_deadline ? formatDate(new Date(competition.reg_deadline), 'yyyy-mm-dd') : '-'}
      </Table.Col>
      <Table.Col>{date}</Table.Col>
    </Table.Row>
  );
}

export function CompetitionListTable({ competitions, isLoading }: Props) {
  return (
    <Table bg="even">
      <Table.Head>
        <Table.Row>
          <Table.Col as="th">Tävling</Table.Col>
          <Table.Col as="th">Status</Table.Col>
          <Table.Col as="th">Tävlingsnivå</Table.Col>
          <Table.Col as="th">Anmäl innan</Table.Col>
          <Table.Col as="th">Datum</Table.Col>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {isLoading ? <Table.Loading cols={5} /> : competitions.map(c => <Row competition={c} key={c.id} />)}
      </Table.Body>
    </Table>
  );
}
